import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { SupportTicketPriority } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { useHandler } from "@redotech/react-util/hook";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useCallback, useState } from "react";
import { useSetPrioritySupportTicketAction } from "../actions/use-support-ticket-actions";
import { FieldLabel } from "./field-label";

const priorityItems: RedoListItem<SupportTicketPriority | null>[] = [
  { type: "text", id: "none", text: "None", value: null },
  {
    type: "badge",
    id: "urgent",
    badge: { color: "error", text: "Urgent" },
    value: "URGENT",
  },
];

export const PrioritySelector = memo(function PrioritySelector({
  supportTicketId,
  priority,
}: {
  supportTicketId: SupportTicketId;
  priority: SupportTicketPriority | null;
}) {
  const setPriority = useSetPrioritySupportTicketAction();
  const [badgeRef, setBadgeRef] = useState<HTMLElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const isUrgent = priority === "URGENT";

  const handleBadgeClick = useHandler(() => {
    setDropdownOpen((open) => !open);
  });

  const handleItemSelected = useCallback(
    ({ item }: { item: RedoListItem<SupportTicketPriority | null> }) => {
      void setPriority.execute(supportTicketId, item.value);
      setDropdownOpen(false);
    },
    [setPriority, supportTicketId],
  );

  const badge = isUrgent ? (
    <RedoBadge color="error" shadow size="sm" text="Urgent" />
  ) : (
    <RedoBadge color="gray" shadow size="sm" text="+" />
  );

  return (
    <Flex align="center" gap="xs" justify="space-between">
      <FieldLabel>Priority</FieldLabel>
      <Flex cursor="pointer" onClick={handleBadgeClick} ref={setBadgeRef}>
        {badge}
      </Flex>
      <RedoListDropdown
        dropdownAnchor={badgeRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor={false}
        items={priorityItems}
        itemSelected={handleItemSelected}
        selectedItems={isUrgent ? "urgent" : "none"}
        selectionVariant="none"
        setDropdownOpen={setDropdownOpen}
        size="xs"
      />
    </Flex>
  );
});
